// Inline source citations in the transcript view (INLINE_CITATIONS_PLAN.md).
//
// Assistant rows carry `message_metadata.citations` — `shown` is the
// resolved public list for the (already renumbered) text, `dropped` is the
// audit of markers the backend removed (internal file / Q&A sources, or
// numbers the turn never produced).
export const CITATION_HREF_PREFIX = '#aimdoc-cite-';

export interface ShownCitation {
    n: number;
    url: string;
    title?: string | null;
    /** Site snippet for the chip's hover preview card (web results; absent for pages). */
    description?: string | null;
    kind?: string;
}

export interface DroppedCitation {
    marker: number;
    reason: 'internal' | 'unresolved' | string;
    kind?: string;
    label?: string;
}

const MARKER_RE = /\[(\d{1,3})\]/g;
const SKIP_PARENTS = new Set(['link', 'linkReference', 'definition', 'inlineCode', 'code']);

const isLinkSyntax = (value: string, start: number, end: number): boolean => {
    const next = value[end];
    if (next === '(' || next === ':') return true;
    // A neighbouring bracket group only makes this a link when that
    // neighbour is not itself a marker: `[1][2]` is two citations.
    if (next === '[' && !/^\[\d{1,3}\]/.test(value.slice(end))) return true;
    return start > 0 && value[start - 1] === ']' && !/\[\d{1,3}\]$/.test(value.slice(0, start));
};

const splitTextNode = (value: string): any[] | null => {
    MARKER_RE.lastIndex = 0;
    const nodes: any[] = [];
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = MARKER_RE.exec(value)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        if (isLinkSyntax(value, start, end)) continue;
        if (start > last) nodes.push({ type: 'text', value: value.slice(last, start) });
        const n = Number(match[1]);
        nodes.push({
            type: 'link',
            url: `${CITATION_HREF_PREFIX}${n}`,
            title: null,
            children: [{ type: 'text', value: String(n) }],
        });
        last = end;
    }
    if (nodes.length === 0) return null;
    if (last < value.length) nodes.push({ type: 'text', value: value.slice(last) });
    return nodes;
};

const walk = (node: any): void => {
    if (!node || !Array.isArray(node.children) || SKIP_PARENTS.has(node.type)) return;
    const next: any[] = [];
    let changed = false;
    for (const child of node.children) {
        if (child?.type === 'text') {
            const replacement = splitTextNode(child.value || '');
            if (replacement) {
                next.push(...replacement);
                changed = true;
                continue;
            }
        }
        walk(child);
        next.push(child);
    }
    if (changed) node.children = next;
};

/** remark plugin: `[n]` text → link node with a `#aimdoc-cite-n` href. */
export const remarkCitations = () => (tree: any) => {
    walk(tree);
};

export const parseCitationHref = (href: unknown): number | null => {
    if (typeof href !== 'string' || !href.startsWith(CITATION_HREF_PREFIX)) return null;
    const n = Number(href.slice(CITATION_HREF_PREFIX.length));
    return Number.isInteger(n) && n > 0 ? n : null;
};

export const shownCitations = (messageMetadata: any): ShownCitation[] => {
    const shown = messageMetadata?.citations?.shown;
    if (!Array.isArray(shown)) return [];
    return shown.filter(
        (c: any) => c && Number.isInteger(c.n) && typeof c.url === 'string' && c.url
    );
};

export const droppedCitations = (messageMetadata: any): DroppedCitation[] => {
    const dropped = messageMetadata?.citations?.dropped;
    if (!Array.isArray(dropped)) return [];
    return dropped.filter((d: any) => d && Number.isInteger(d.marker));
};

/** True when a trailing `[…` in streamed text is a complete citation marker. */
export const endsWithCitationMarker = (tail: string): boolean => /^\[\d{1,3}\]/.test(tail);
